import React, { useContext, useEffect, useState } from 'react';
import { Link, Prompt, useHistory, useLocation, useParams } from 'react-router-dom';
import { filter, find, indexOf, map, isEmpty, isEqual } from 'lodash';

import { v4 } from 'uuid';
import cogoToast from 'cogo-toast';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';

import { StateContext } from '../../../../App';
import { routes } from '../../../data/constants';
import { coreTypes } from 'dealer-website-components';

import { initialState, initialAdvertisementContent, reducer } from '../reducer';
import {
  advertisementInitializeHandler,
  advertisementSetHandler,
  advertisementSetDescriptionHandler,
  advertisementSetContentHandler,
  advertisementSetGroupHandler,
  advertisementSetCampaignHandler,
  advertisementSetPrimaryHandler,
  advertisementSetFiltersHandler,
  advertisementUndoChangesHandler,
  advertisementFiltersModalHandler
} from '../actions';

import GroupMenu from '../../groups/components/GroupMenu';
import FiltersModal from '../../../builder/editors/FiltersModal';

import ButtonWithIndicator from '../../../../components/common/ButtonWithIndicator';
import Dropdown from '../../../../components/common/Dropdown';
import TextInput from '../../../builder/editors/components/TextInput';
import WysiwygEditor from '../../../../components/common/WysiwygEditor';

import LoadingSkeleton from './LoadingSkeleton';
import { getCampaignStatus } from '../../../../utils';
import CampaignStatusBadge from '../../components/CampaignStatusBadge';

const { FilterTypes } = coreTypes;

export const AdvertisementQuery = `query Advertisements($data: AdvertisementSearchInputType!) { 
  advertisements(data: $data) { 
    id
    description
    content{
      id
      data
      culture
    }
    groups
    filters
    isDraft
    disabled
    campaignId
    isPrimary
    locked
    source
  }
}`;

export const AdvertisementsInGroupsQuery = `query Advertisements {
  advertisements {
    id
    description
    groups
    isPrimary
  }
}`;

export const CampaignsQuery = `query Campaigns {
  campaigns {
    id
    name
    start
    stop
    locked
  }
}`;

export const AdSaveMutation = `mutation AdvertisementUpdate($data: AdvertisementUpdateInputType!) {
  advertisementUpdate(data: $data) {
      ok
  }
}`;

const Detail = ({ context }) => {
  const [state, dispatch] = React.useReducer(reducer, initialState);
  const globalState = useContext(StateContext);
  const location = useLocation();
  const history = useHistory();

  const [advertisementLanguage] = useState(location?.state?.language ? location.state.language : context.culture);
  const advertisement = state.advertisement;
  const languages = globalState?.configuration?.website?.languages;

  const [isGroupOpen, setIsGroupOpen] = useState(true);
  const [campaigns, setCampaigns] = useState(undefined);
  const [savedAdvertisement, setSavedAdvertisement] = useState(undefined);
  const [advertisementsInGroups, setAdvertisementsInGroups] = useState([]);

  const { id: idFromUrl } = useParams();
  const [formErrors, setFormErrors] = useState({});

  const tabClassNames =
    '-builder-mb-px builder-px-3 builder-py-2.5 builder-cursor-pointer builder-text-sm builder-font-medium builder-uppercase builder-text-slate-500 builder-border-b-2 builder-border-transparent builder-rounded-t builder-transition-colors hover:builder-text-slate-800 aria-selected:builder-text-slate-800 aria-selected:builder-font-semibold aria-selected:builder-border-primary focus:builder-outline-none focus-visible:builder-ring-2 focus-visible:builder-ring-primary';

  const fetchAd = (id) => {
    fetch('/graphql', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ query: AdvertisementQuery, variables: { data: { id: id } } })
    })
      .then((resp) => resp.json())
      .then((result) => {
        const ad = result && result.data && result.data.advertisements && result.data.advertisements.length > 0 ? result.data.advertisements[0] : undefined;
        if (ad) {
          advertisementSetHandler(dispatch, ad);
        } else {
          advertisementInitializeHandler(dispatch, id, location?.state?.groupId ? [location.state.groupId] : []);
        }
      })
      .catch((err) => {});
  };

  const fetchAdvertisementsInGroups = () => {
    fetch('/graphql', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ query: AdvertisementsInGroupsQuery })
    })
      .then((resp) => resp.json())
      .then((result) => {
        setAdvertisementsInGroups(result?.data?.advertisements || []);
      })
      .catch((err) => {});
  };

  const fetchCampaigns = () => {
    fetch('/graphql', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ query: CampaignsQuery })
    })
      .then((resp) => resp.json())
      .then((result) => {
        setCampaigns(result?.data?.campaigns || []);
      })
      .catch((err) => {
        setCampaigns([]);
      });
  };

  const handleFiltersModal = () => {
    advertisementFiltersModalHandler(dispatch, { ...state.filtersModal, isOpen: !state.filtersModal.isOpen });
  };

  const handleChangeFilters = (filters) => {
    advertisementSetFiltersHandler(dispatch, filters);
  };

  const validateForm = () => {
    let isValid = false;
    let errors = {};

    // No empty values
    if (state.advertisement.description === '') {
      errors['adDescription'] = 'The advertisement description cannot be empty.';
      isValid = false;
    }

    if (isEmpty(errors)) {
      isValid = true;
    }

    return {
      isValid,
      errors
    };
  };

  const isValid = () => {
    setFormErrors({});
    const { isValid, errors } = validateForm();
    setFormErrors(errors);
    return isValid;
  };

  const saveAd = (advertisement) => {
    if (isValid()) {
      const variables = {
        data: {
          id: advertisement.id,
          description: advertisement.description,
          content: map(advertisement.content, (c) => ({
            id: c.id,
            culture: c.culture,
            data: c.data
          })),
          groups: advertisement.groups,
          filters: JSON.stringify(advertisement.filters),
          disabled: advertisement.disabled,
          isDraft: advertisement.isDraft,
          campaignId: advertisement.campaignId || null,
          isPrimary: !!advertisement.isPrimary
        }
      };

      fetch('/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ query: AdSaveMutation, variables: variables })
      })
        .then((resp) => resp.json())
        .then((result) => {
          if (result?.data?.advertisementUpdate?.ok) {
            cogoToast.success('Advertisement Saved');
            setSavedAdvertisement(advertisement);
            fetchAdvertisementsInGroups();
          } else {
            const error = result && result.errors && result.errors.length > 0 ? result.errors[0].message : 'Something went wrong';
            cogoToast.error(error);
          }
        })
        .catch((err) => {});
    }
  };

  useEffect(() => {
    fetchAd(idFromUrl);
    fetchCampaigns();
    fetchAdvertisementsInGroups();
  }, []);

  useEffect(() => {
    if (advertisement && savedAdvertisement === undefined) setSavedAdvertisement(advertisement);
  }, [advertisement, savedAdvertisement]);

  if (state.advertisement === undefined || state.advertisementLoading) {
    return <LoadingSkeleton />;
  }

  const hasUnsavedChanges = savedAdvertisement !== undefined && !isEqual(advertisement, savedAdvertisement);
  const overviewGroupId = location?.state?.fromGroupId || advertisement.groups[0];
  const overviewUrl = overviewGroupId ? `${routes.ADVERTISEMENTS}/${overviewGroupId}` : routes.ADVERTISEMENTS;

  const selectedCampaign = find(campaigns, (c) => c.id === advertisement.campaignId);
  const selectableCampaigns = filter(campaigns, (c) => !c.locked || c.id === advertisement.campaignId);
  const campaignOptions = [{ key: null, value: 'No campaign' }, ...map(selectableCampaigns, (c) => ({ key: c.id, value: c.name, campaign: c }))];

  const getCampaignHint = () => {
    if (!advertisement.campaignId) return { className: 'builder-text-gray-500', text: 'Without a campaign the advertisement is always shown.' };
    if (!selectedCampaign) {
      return campaigns ? { className: 'builder-text-yellow-700', text: 'The linked campaign is not available in this environment. The advertisement is still only shown during that campaign.' } : undefined;
    }

    const campaignStatus = getCampaignStatus(selectedCampaign);
    if (campaignStatus === 'expired') return { className: 'builder-text-red-600', text: `This campaign ended on ${selectedCampaign.stop}, the advertisement is no longer shown.` };
    if (campaignStatus === 'upcoming') return { className: 'builder-text-yellow-700', text: `This campaign starts on ${selectedCampaign.start}, the advertisement is not shown before that.` };
    return { className: 'builder-text-gray-500', text: `Only shown from ${selectedCampaign.start} to ${selectedCampaign.stop}.` };
  };
  const campaignHint = getCampaignHint();

  return (
    <React.Fragment>
      <Prompt when={hasUnsavedChanges} message='You have unsaved changes. If you leave this page, they will be lost. Do you want to leave?' />
      <div className='builder-flex builder-flex-col builder-flex-1 builder-p-5'>
        <div className='builder-flex builder-flex-row builder-items-center builder-justify-between builder-mb-5'>
          <div className='builder-flex builder-flex-row builder-items-center'>
            <Link to={overviewUrl} className='builder-px-4 builder-py-2 builder-bg-primary builder-text-white builder-border builder-border-bg-blue-500 builder-cursor-pointer opacity-100 builder-mr-4'>
              <i className='fa fa-chevron-left builder-mr-2' />
              Overview
            </Link>
            <div className='builder-text-2xl builder-text-black builder-font-bold builder-mr-4'>Advertisement: {advertisement.description}</div>
          </div>
          {advertisement.locked && (
            <span className='builder-font-bold' title='Synced advertisements can only be changed in the source builder'>
              <i className='fal fa-lock builder-mr-2' />
              Synced from {advertisement.source}, read-only
            </span>
          )}
          {!advertisement.locked && (
            <div className='flex'>
              {state.history === undefined && (
                <ButtonWithIndicator
                  loading={false}
                  onClick={() => history.push(overviewUrl)}
                  icon='fal fa-times'
                  text='Cancel'
                  colorClass='builder-bg-primary builder-text-white'
                  borderClass='builder-border builder-border-bg-blue-500'
                  className='builder-ml-0 builder-mr-0'
                />
              )}
              {state.history && (
                <ButtonWithIndicator
                  loading={false}
                  disabled={state.history.length === 1}
                  onClick={() => advertisementUndoChangesHandler(dispatch)}
                  icon='fal fa-undo'
                  text='Undo changes'
                  colorClass='builder-bg-primary builder-text-white'
                  borderClass='builder-border builder-border-bg-blue-500'
                  className='builder-ml-0 builder-mr-0'
                />
              )}

              <ButtonWithIndicator
                loading={false}
                onClick={() => saveAd(state.advertisement)}
                icon='fal fa-save'
                text='Save'
                colorClass='builder-bg-primary builder-text-white'
                borderClass='builder-border builder-border-bg-blue-500'
                className='builder-ml-0 builder-mr-0'
              />
            </div>
          )}
        </div>
        <div className='builder-flex builder-p-5 builder-w-full h-full builder-bg-gray-50'>
          <div className='builder-w-3/4 builder-mr-5'>
            <div className='builder-flex builder-flex-col builder-mb-5'>
              <div className='builder-flex builder-flex-col builder-mb-5'>
                <div className='builder-text-black builder-text-md builder-font-bold builder-mb-2'>Description</div>
                <TextInput
                  placeHolder='Add description'
                  className={`builder-flex builder-flex-1 builder-border builder-w-full builder-rounded-md ${formErrors['adDescription'] ? 'builder-border-red-500' : 'builder-border-gray-300'}`}
                  value={advertisement.description}
                  disabled={advertisement.locked}
                  onChanged={(value) => {
                    advertisementSetDescriptionHandler(dispatch, value);
                  }}
                />
                {formErrors['adDescription'] && <span className='builder-mt-1 builder-text-sm builder-text-red-500'>{formErrors['adDescription']}</span>}
              </div>
              <Tabs defaultIndex={indexOf(languages, advertisementLanguage)}>
                <TabList className='builder-flex builder-gap-1 builder-border-b builder-border-slate-200'>
                  {map(languages, (language) => {
                    return (
                      <React.Fragment>
                        <Tab className={tabClassNames}>{language}</Tab>
                      </React.Fragment>
                    );
                  })}
                </TabList>

                {map(languages, (language) => {
                  const currentAdContent = find(advertisement?.content, (content) => content.culture === language);
                  return (
                    <TabPanel>
                      <div className='builder-pt-4'>
                        <WysiwygEditor
                          data={currentAdContent?.data || initialAdvertisementContent.data}
                          readOnly={advertisement.locked}
                          showVariables
                          showCampaignVariables
                          variableScope={{ campaign: selectedCampaign }}
                          onChange={(newValue) => {
                            if (currentAdContent) {
                              const otherAdContent = filter(advertisement.content, (content) => content.id !== currentAdContent.id);
                              const contentValue = [...otherAdContent, { ...currentAdContent, data: newValue }];
                              advertisementSetContentHandler(dispatch, contentValue);
                            } else {
                              const contentValue = [...advertisement.content, { ...initialAdvertisementContent, id: v4(), culture: language, data: newValue }];
                              advertisementSetContentHandler(dispatch, contentValue);
                            }
                          }}
                        />
                      </div>
                    </TabPanel>
                  );
                })}
              </Tabs>
            </div>
          </div>

          <div className={`builder-w-1/4 ${advertisement.locked ? 'builder-pointer-events-none builder-opacity-60' : ''}`}>
            <div className={`builder-pt-2 builder-pb-4 builder-bg-white builder-mb-5 ${isGroupOpen ? 'builder-border-b builder-border-gray-300 builder-shadow-sm' : 'builder-mb-2'}`}>
              <div className='builder-flex builder-justify-between builder-items-center builder-cursor-pointer' onClick={() => setIsGroupOpen(!isGroupOpen)}>
                <h3 className='builder-text-black builder-text-md builder-font-bold builder-ml-5'>Groups</h3>
                <i className={`fas builder-mr-5 builder-text-md ${isGroupOpen ? 'fa-caret-up' : 'fa-caret-down'}`} />
              </div>
              <div className={`builder-px-5 ${isGroupOpen ? 'builder-flex builder-flex-col ' : 'builder-hidden'}`}>
                <GroupMenu
                  advertisement={advertisement}
                  advertisements={advertisementsInGroups}
                  onSelectGroup={(groupId) => {
                    advertisementSetGroupHandler(dispatch, [...advertisement.groups, groupId]);
                  }}
                  onDeselectGroup={(groupId) => {
                    advertisementSetGroupHandler(
                      dispatch,
                      filter(advertisement.groups, (g) => g !== groupId)
                    );
                  }}
                  context={context}
                />
              </div>
            </div>
            <div className='builder-pt-2 builder-pb-4 builder-bg-white builder-mb-5 builder-border-b builder-border-gray-300 builder-shadow-sm'>
              <div className='builder-flex builder-justify-between builder-items-center'>
                <h3 className='builder-text-black builder-text-md builder-font-bold builder-ml-5'>Primary</h3>
              </div>
              <label className='builder-flex builder-items-start builder-px-5 builder-mt-2 builder-cursor-pointer'>
                <input
                  type='checkbox'
                  checked={!!advertisement.isPrimary}
                  onChange={(e) => advertisementSetPrimaryHandler(dispatch, e.target.checked)}
                  className='builder-mt-1 builder-h-4 builder-w-4 builder-cursor-pointer'
                  data-testid='advertisement-primary'
                />
                <span className='builder-ml-2 builder-text-sm builder-text-gray-700'>Always show this advertisement first in its groups. Only one advertisement per group can be primary.</span>
              </label>
            </div>
            <div className={`builder-pt-2 builder-pb-4 builder-bg-white builder-mb-5 builder-border-b builder-border-gray-300 builder-shadow-sm'}`}>
              <div className='builder-flex builder-justify-between builder-items-center'>
                <h3 className='builder-text-black builder-text-md builder-font-bold builder-ml-5'>Filters</h3>
              </div>
              <div className={`builder-px-5 ${isGroupOpen ? 'builder-flex builder-flex-col ' : 'builder-hidden'}`}>
                <div className='mt-5 builder-cursor-pointer' onClick={() => handleFiltersModal()}>{`Edit filters (${advertisement.filters.length})`}</div>

                <FiltersModal
                  isOpen={state.filtersModal.isOpen}
                  filters={advertisement.filters ? advertisement.filters : []}
                  // availableFilterTypes={[FilterTypes.makeModel, FilterTypes.label, FilterTypes.fuel]}
                  onClose={(filters) => {
                    handleChangeFilters([...filters]);
                    handleFiltersModal();
                  }}
                />
              </div>
            </div>
            <div className='builder-pt-2 builder-pb-4 builder-bg-white builder-mb-5 builder-border-b builder-border-gray-300 builder-shadow-sm'>
              <div className='builder-flex builder-justify-between builder-items-center'>
                <h3 className='builder-text-black builder-text-md builder-font-bold builder-ml-5'>Campaign</h3>
                {selectedCampaign && (
                  <Link to={`${routes.CAMPAIGN_DETAIL}/${selectedCampaign.id}`} className='builder-flex builder-items-center builder-mr-5 builder-text-sm builder-text-black hover:builder-underline' title='Open campaign'>
                    Open campaign
                    <i className='fal fa-external-link-alt builder-ml-2' style={{ fontSize: 12 }} />
                  </Link>
                )}
              </div>
              <div className='builder-flex builder-flex-col builder-px-5 builder-mt-2'>
                <Dropdown
                  options={campaignOptions}
                  value={advertisement.campaignId || null}
                  maxWidth='100%'
                  placeHolder='Select a campaign'
                  borderClassName='builder-border builder-border-gray-300 builder-rounded-md'
                  selectClassName='builder-py-2 builder-rounded-md'
                  renderItem={(item) => (
                    <div className={`builder-flex builder-justify-between builder-p-2 ${!item.key ? 'builder-bg-gray-200 builder-text-gray-500' : ''}`} onClick={() => advertisementSetCampaignHandler(dispatch, item.key)}>
                      <span className='builder-text-sm'>{item.value}</span>
                      {item.campaign && (
                        <span className='builder-flex builder-items-center builder-text-sm builder-text-gray-500 builder-whitespace-nowrap builder-ml-2'>
                          {item.campaign.start} - {item.campaign.stop}
                          <CampaignStatusBadge campaign={item.campaign} className='builder-ml-2' />
                          <Link to={`${routes.CAMPAIGN_DETAIL}/${item.campaign.id}`} onClick={(e) => e.stopPropagation()} className='builder-ml-3 builder-text-black hover:builder-text-primary' title='Open campaign'>
                            <i className='fal fa-external-link-alt' style={{ fontSize: 12 }} />
                          </Link>
                        </span>
                      )}
                    </div>
                  )}
                />
                {campaignHint && <span className={`builder-mt-2 builder-text-sm ${campaignHint.className}`}>{campaignHint.text}</span>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default Detail;
